import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Alert, Button, Card, Checkbox, Dialog, EmptyState, ErrorState, Input, Select, StatusBadge, TextField, TextSkeleton, Textarea, cn,
} from '@dru/ui';
import {
  FIELD_TYPES, TEMPLATE_CONTEXTS, TEMPLATE_CONTEXT_LABEL, renderTemplate, validateTemplateShape,
  type FieldType, type TemplateChoice, type TemplateContext, type TemplateField, type TemplateOption,
} from '@dru/clinical-math/smart-template';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';

/**
 * The clinical shortcut / smart template master (Requirement 4).
 *
 * Platform wording, offered in every clinician's composer. A template is
 * never deleted: deactivating takes it out of the composers, and every edit
 * bumps its version. Fixed sentences are quick-select options, which a
 * clinician has to press; a pattern sentence must have a blank, and is
 * written only when the blank is filled.
 */
interface Row {
  id: string; code: string; shortcut: string; title: string; titleHi: string | null; contexts: TemplateContext[];
  sentences: string[]; fields: TemplateField[]; options: TemplateOption[]; isActive: boolean; version: number; sortOrder: number;
}

export function ClinicalTemplates() {
  const [context, setContext] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState<Row | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = useQuery<{ items: Row[] }>(
    () => api.get(`/admin/clinical-templates?${context ? `context=${context}&` : ''}${showAll ? 'state=all' : ''}`),
    [context, showAll]);

  async function setActive(r: Row, active: boolean) {
    setError(null);
    try { await api.post(`/admin/clinical-templates/${r.id}/${active ? 'activate' : 'deactivate'}`, {}); list.reload(); }
    catch (e) { setError(errorMessage(e)); }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select aria-label="Where it is offered" value={context} onChange={(e) => setContext(e.target.value)} className="h-9 w-64 max-md:w-full">
          <option value="">Every context</option>
          {TEMPLATE_CONTEXTS.map((c) => <option key={c} value={c}>{TEMPLATE_CONTEXT_LABEL[c]}</option>)}
        </Select>
        <Checkbox label="Show deactivated" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
        <div className="flex-1" />
        <Button icon={<Plus aria-hidden className="h-4 w-4" />} onClick={() => setEditing('new')} className="max-md:w-full">New template</Button>
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      {list.loading && !list.data ? <TextSkeleton lines={6} />
        : list.error ? <ErrorState detail={list.error} onRetry={list.reload} />
        : !list.data?.items.length ? <EmptyState title="No templates" hint="Templates are offered in the clinicians' composers." />
        : (
          <Card>
            <ul className="divide-y divide-neutral-200">
              {list.data.items.map((r) => (
                <li key={r.id} className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3', !r.isActive && 'opacity-60')}>
                  <div className="min-w-0 flex-[1_1_16rem]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-small font-medium text-neutral-900">{r.title}</span>
                      {r.titleHi && <span className="text-caption text-neutral-500">{r.titleHi}</span>}
                      <span className="font-mono text-xs text-neutral-600">{r.shortcut}</span>
                      {!r.isActive && <StatusBadge tone="neutral">Deactivated</StatusBadge>}
                    </div>
                    <div className="text-caption text-neutral-500">
                      {r.contexts.map((c) => TEMPLATE_CONTEXT_LABEL[c] ?? c).join(' · ')} · {r.options.length} option{r.options.length === 1 ? '' : 's'}
                      {' '}· {r.sentences.length} sentence{r.sentences.length === 1 ? '' : 's'} · v{r.version}
                    </div>
                  </div>
                  <div className="flex gap-1.5 max-md:w-full">
                    <Button size="sm" variant="secondary" onClick={() => setEditing(r)}>Edit</Button>
                    {r.isActive
                      ? <Button size="sm" variant="ghost" onClick={() => setActive(r, false)}>Deactivate</Button>
                      : <Button size="sm" variant="ghost" onClick={() => setActive(r, true)}>Activate</Button>}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      {editing && (
        <TemplateEditor row={editing === 'new' ? null : editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); list.reload(); }} />
      )}
    </div>
  );
}

/** "VALUE | Label | words written" per line. */
function choicesToText(c: TemplateChoice[] = []) {
  return c.map((x) => [x.value, x.label, x.text ?? ''].join(' | ').replace(/ \| $/, '')).join('\n');
}
function textToChoices(t: string): TemplateChoice[] {
  return t.split('\n').map((l) => l.split('|').map((p) => p.trim())).filter((p) => p[0])
    .map(([value, label, text]) => ({ value: value!.toUpperCase().replace(/\s+/g, '_'), label: label || value!, ...(text ? { text } : {}) }));
}

function TemplateEditor({ row, onClose, onSaved }: { row: Row | null; onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState(row?.code ?? '');
  const [shortcut, setShortcut] = useState(row?.shortcut ?? '/');
  const [title, setTitle] = useState(row?.title ?? '');
  const [titleHi, setTitleHi] = useState(row?.titleHi ?? '');
  const [contexts, setContexts] = useState<TemplateContext[]>(row?.contexts ?? ['CONSULTATION']);
  const [options, setOptions] = useState<TemplateOption[]>(row?.options ?? []);
  const [sentences, setSentences] = useState((row?.sentences ?? []).join('\n'));
  const [fields, setFields] = useState<TemplateField[]>(row?.fields ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shape = { sentences: sentences.split('\n').map((s) => s.trim()).filter(Boolean), fields, options };
  const problems = validateTemplateShape(shape);
  const preview = renderTemplate(shape, { selected: options.map((o) => o.code).slice(0, 1) });

  const setOpt = (i: number, patch: Partial<TemplateOption>) => setOptions((o) => o.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const setField = (i: number, patch: Partial<TemplateField>) => setFields((f) => f.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  async function save() {
    setBusy(true); setError(null);
    const clean = (o: TemplateOption) => ({ ...o, labelHi: o.labelHi || undefined, group: o.group || undefined });
    const body = {
      shortcut, title, titleHi: titleHi || null, contexts, ...shape, options: shape.options.map(clean),
    };
    try {
      if (row) await api.patch(`/admin/clinical-templates/${row.id}`, { ...body, expectedVersion: row.version });
      else await api.post('/admin/clinical-templates', { ...body, code }, crypto.randomUUID());
      onSaved();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog
      wide title={row ? `Edit ${row.title}` : 'New clinical template'} onClose={onClose}
      info="Options are fixed sentences a clinician presses. Sentences use {blanks} named after fields, and are written only when every blank in them is filled."
      footer={(
        <div className="flex w-full justify-end gap-2 max-md:flex-col-reverse max-md:items-stretch">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={busy} disabled={problems.length > 0 || !title.trim() || contexts.length === 0 || (!row && !code.trim())} onClick={save}>
            {row ? `Save as version ${row.version + 1}` : 'Create'}
          </Button>
        </div>
      )}
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {!row && <TextField label="Code" value={code} onChange={(v) => setCode(v.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} required />}
          <TextField label="Shortcut" value={shortcut} onChange={(v) => setShortcut(v.toLowerCase())} required />
          <TextField label="Title" value={title} onChange={setTitle} required />
          <TextField label="Title in Hindi (optional)" value={titleHi} onChange={setTitleHi} />
        </div>

        <fieldset>
          <legend className="text-caption text-neutral-700">Offered in</legend>
          <div className="mt-1 grid gap-1 sm:grid-cols-2">
            {TEMPLATE_CONTEXTS.map((c) => (
              <Checkbox key={c} label={TEMPLATE_CONTEXT_LABEL[c]} checked={contexts.includes(c)}
                onChange={(e) => setContexts((s) => (e.target.checked ? [...s, c] : s.filter((x) => x !== c)))} />
            ))}
          </div>
        </fieldset>

        <section>
          <div className="flex items-center justify-between">
            <h3 className="text-caption text-neutral-700">Quick-select options</h3>
            <Button size="sm" variant="ghost" icon={<Plus aria-hidden className="h-3.5 w-3.5" />}
              onClick={() => setOptions((o) => [...o, { code: `OPTION_${o.length + 1}`, label: '', sentence: '' }])}>Add option</Button>
          </div>
          <div className="mt-1 space-y-2">
            {options.map((o, i) => (
              <div key={i} className="grid gap-1.5 rounded-input border border-neutral-200 p-2 sm:grid-cols-[8rem_1fr_1fr_6rem_auto]">
                <Input aria-label="Code" value={o.code} onChange={(e) => setOpt(i, { code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} />
                <Input aria-label="Button label" placeholder="Button label" value={o.label} onChange={(e) => setOpt(i, { label: e.target.value })} />
                <Input aria-label="Hindi label" placeholder="Hindi label" value={o.labelHi ?? ''} onChange={(e) => setOpt(i, { labelHi: e.target.value })} />
                <Input aria-label="Exclusive group" placeholder="Group" value={o.group ?? ''} onChange={(e) => setOpt(i, { group: e.target.value })} />
                <Button size="sm" variant="ghost" aria-label="Remove option" onClick={() => setOptions((x) => x.filter((_, j) => j !== i))}>
                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                </Button>
                <Input aria-label="Sentence it writes" placeholder="The full sentence it writes" value={o.sentence}
                  onChange={(e) => setOpt(i, { sentence: e.target.value })} className="sm:col-span-5" />
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-caption text-neutral-700">Sentences with blanks (one per line)</h3>
          <Textarea rows={3} value={sentences} onChange={(e) => setSentences(e.target.value)} className="mt-1 font-mono text-small"
            placeholder="The employee reports {complaint} for the past {duration}." />
        </section>

        <section>
          <div className="flex items-center justify-between">
            <h3 className="text-caption text-neutral-700">Fields (the blanks)</h3>
            <Button size="sm" variant="ghost" icon={<Plus aria-hidden className="h-3.5 w-3.5" />}
              onClick={() => setFields((f) => [...f, { key: `field${f.length + 1}`, label: '', type: 'text' }])}>Add field</Button>
          </div>
          <div className="mt-1 space-y-2">
            {fields.map((f, i) => (
              <div key={i} className="grid gap-1.5 rounded-input border border-neutral-200 p-2 sm:grid-cols-[8rem_1fr_1fr_8rem_auto]">
                <Input aria-label="Key" value={f.key} onChange={(e) => setField(i, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })} />
                <Input aria-label="Label" placeholder="Label" value={f.label} onChange={(e) => setField(i, { label: e.target.value })} />
                <Input aria-label="Hindi label" placeholder="Hindi label" value={f.labelHi ?? ''} onChange={(e) => setField(i, { labelHi: e.target.value || undefined })} />
                <Select aria-label="Type" value={f.type} onChange={(e) => setField(i, { type: e.target.value as FieldType })}>
                  {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
                <Button size="sm" variant="ghost" aria-label="Remove field" onClick={() => setFields((x) => x.filter((_, j) => j !== i))}>
                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                </Button>
                {f.type === 'unit' && (
                  <Input aria-label="Unit" placeholder="Unit, e.g. mg" value={f.unit ?? ''} onChange={(e) => setField(i, { unit: e.target.value })} className="sm:col-span-5" />
                )}
                {f.type === 'choice' && (
                  <Textarea aria-label="Choices" rows={3} className="font-mono text-small sm:col-span-5"
                    placeholder={'VALUE | Label | words written\nMILD | Mild | mild'}
                    defaultValue={choicesToText(f.choices)} onBlur={(e) => setField(i, { choices: textToChoices(e.target.value) })} />
                )}
              </div>
            ))}
          </div>
        </section>

        {problems.length > 0 ? (
          <Alert tone="warning" title="Not ready to save">
            <ul className="ml-4 list-disc">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </Alert>
        ) : (
          <div className="rounded-input border border-neutral-200 bg-neutral-50 p-2">
            <p className="text-caption text-neutral-500">With the first option pressed and nothing filled, it writes</p>
            <p className="text-small text-neutral-900">{preview.text || '— nothing —'}</p>
          </div>
        )}
        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </Dialog>
  );
}
